import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { Button, Textarea } from './ui';
import { IconDownload, IconX } from './icons';
import { useOpenDocument } from './Layout';

type Msg = { role: 'user' | 'assistant'; content: string; error?: boolean; tools?: string[] };

const SUGGESTIONS = ['כמה מכרנו החודש לעומת החודש שעבר?', 'מי חייב לי יותר מ-60 יום?', 'מה 10 המוצרים הכי רווחיים ברבעון?', 'מה עומד להיגמר במלאי?', 'אילו הצעות מחיר מחכות לתשובה?'];
const STORE = 'markit:assistant';

// ------------------------------------------------------------------ a small markdown renderer (tables, lists, bold, links)

function inline(text: string, openDoc: (id: number) => void, close: () => void): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={k++}>{m[1]}</strong>);
    else {
      const href = m[3]!;
      const doc = /^doc:(\d+)$/.exec(href);
      if (doc) out.push(<button key={k++} type="button" className="link-button" onClick={() => openDoc(Number(doc[1]))}>{m[2]}</button>);
      else if (href.startsWith('/')) out.push(<Link key={k++} to={href} onClick={close}>{m[2]}</Link>);
      else out.push(m[2]);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (line: string) => line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
const plain = (s: string) => s.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

/** A table from the answer as CSV (opens in Excel; BOM so Hebrew shows right). */
function downloadCsv(rows: string[][]) {
  const csv = rows.map((r) => r.map((c) => `"${plain(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `markit-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Answer({ text, openDoc, close }: { text: string; openDoc: (id: number) => void; close: () => void }) {
  const lines = text.split('\n');
  const blocks: ReactNode[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (/^\s*\|/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|/.test(lines[i]!)) {
        if (!/^\s*\|?\s*:?-{2,}/.test(lines[i]!)) rows.push(cells(lines[i]!));
        i++;
      }
      i--;
      const [head, ...body] = rows;
      blocks.push(
        <div key={i} className="assistant-table">
          <div className="table-wrap">
            <table className="table">
              {head ? <thead><tr>{head.map((c, j) => <th key={j}>{inline(c, openDoc, close)}</th>)}</tr></thead> : null}
              <tbody>{body.map((r, j) => <tr key={j}>{r.map((c, x) => <td key={x} className={/^[-₪\d.,%\s]+$/.test(plain(c)) ? 'num tabular' : undefined}>{inline(c, openDoc, close)}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <button type="button" className="link-button small" onClick={() => downloadCsv(rows)}><IconDownload /> Excel</button>
        </div>,
      );
    } else if (/^\s*[-*•]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*[-*•]\s+/, ''));
      i--;
      blocks.push(<ul key={i}>{items.map((t, j) => <li key={j}>{inline(t, openDoc, close)}</li>)}</ul>);
    } else if (/^#{1,4}\s/.test(line)) {
      blocks.push(<div key={i} className="strong">{inline(line.replace(/^#+\s/, ''), openDoc, close)}</div>);
    } else if (line.trim()) {
      blocks.push(<p key={i}>{inline(line, openDoc, close)}</p>);
    }
  }
  return <>{blocks.map((b, i) => <Fragment key={i}>{b}</Fragment>)}</>;
}

// ------------------------------------------------------------------ the panel

/** The smart assistant: a button on every screen, a panel with the conversation. */
export function Assistant() {
  const can = useCan();
  const allowed = can('assistant.use');
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(STORE) ?? '[]') as Msg[];
    } catch {
      return [];
    }
  });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const openDoc = useOpenDocument();
  const status = useQuery({ queryKey: ['assistant'], queryFn: () => api<{ connected: boolean; tools: string[] }>('/api/assistant'), enabled: allowed && open, staleTime: 60_000 });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE, JSON.stringify(msgs.slice(-30)));
    } catch {
      /* private mode */
    }
    end.current?.scrollIntoView({ block: 'end' });
  }, [msgs, busy]);

  if (!allowed) return null;

  const send = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    const history = msgs.filter((m) => !m.error).slice(-10).map(({ role, content }) => ({ role, content }));
    setMsgs((m) => [...m, { role: 'user', content: question }]);
    setText('');
    setBusy(true);
    try {
      const r = await api<{ answer: string; tools: string[] }>('/api/assistant/ask', { body: { question, history } });
      setMsgs((m) => [...m, { role: 'assistant', content: r.answer, tools: r.tools }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: 'assistant', content: (e as Error).message, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open ? (
        <button type="button" className="assistant-fab no-print" onClick={() => setOpen(true)} aria-label="עוזר חכם">
          ✨ <span>עוזר</span>
        </button>
      ) : (
        <aside className="assistant-panel no-print" aria-label="עוזר חכם">
          <header className="assistant-head">
            <strong>✨ עוזר חכם</strong>
            <span className="muted small">שאלות על הנתונים — קריאה בלבד</span>
            <span className="spacer" />
            {msgs.length ? <button type="button" className="link-button small" onClick={() => setMsgs([])}>שיחה חדשה</button> : null}
            <button type="button" className="btn icon ghost sm" onClick={() => setOpen(false)} aria-label="סגירה"><IconX /></button>
          </header>
          <div className="assistant-body">
            {status.data && !status.data.connected ? (
              <div className="callout warn small">העוזר צריך את מפתח Anthropic — מחברים ב<Link to="/settings" onClick={() => setOpen(false)}>הגדרות ← סוכן קריאה</Link>.</div>
            ) : null}
            {!msgs.length ? (
              <div className="stack" style={{ gap: 8 }}>
                <div className="muted small">אפשר לשאול למשל:</div>
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="assistant-chip" onClick={() => send(s)}>{s}</button>
                ))}
              </div>
            ) : null}
            {msgs.map((m, i) => (
              <div key={i} className={`assistant-msg ${m.role}${m.error ? ' error' : ''}`}>
                {m.role === 'assistant' && !m.error ? <Answer text={m.content} openDoc={openDoc} close={() => undefined} /> : m.content}
              </div>
            ))}
            {busy ? <div className="assistant-msg assistant"><span className="spinner" /> בודק בנתונים…</div> : null}
            <div ref={end} />
          </div>
          <form
            className="assistant-input"
            onSubmit={(e) => {
              e.preventDefault();
              send(text);
            }}
          >
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              placeholder="שאלה בעברית…"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(text);
                }
              }}
            />
            <Button type="submit" variant="primary" loading={busy} disabled={!text.trim()}>שליחה</Button>
          </form>
        </aside>
      )}
    </>
  );
}
